import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, TouchableOpacity, Platform, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { withAlpha } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { notificationVisual } from '../config/notificationVisuals';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobileBrowser = isWeb && (screenWidth < 768 || screenHeight < 768);
const isDesktopWeb = isWeb && !isMobileBrowser;

export interface NotificationProps {
  type: 'success' | 'error' | 'info' | 'warning' | 'message';
  title: string;
  message: string;
  visible: boolean;
  onClose: () => void;
  duration?: number;
  action?: {
    label: string;
    onPress: () => void;
    /** For 'message': the notification type (icon/colour). */
    channel?: string;
    channelName?: string;
    data?: Record<string, any>;
  };
}

const STATUS: Record<Exclude<NotificationProps['type'], 'message'>, { icon: string; color: () => string }> = {
  success: { icon: 'checkmark-circle', color: () => colors.success as string },
  error: { icon: 'alert-circle', color: () => colors.error as string },
  warning: { icon: 'warning', color: () => colors.warning as string },
  info: { icon: 'information-circle', color: () => colors.info as string },
};

/**
 * The app-wide toast: confirmations/errors (success · error · warning ·
 * info) and incoming notifications ('message' — icon and colour of the
 * notification type; tapping the card opens it). Bottom-right on desktop
 * web, top on phones.
 */
export const Notification: React.FC<NotificationProps> = ({
  type,
  title,
  message,
  visible,
  onClose,
  duration = 5000,
  action,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const offset = isDesktopWeb ? 40 : -40;
  const slideAnim = useRef(new Animated.Value(offset)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    slideAnim.setValue(offset);
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: 0, duration: 220, useNativeDriver: true }),
      Animated.timing(opacityAnim, { toValue: 1, duration: 220, useNativeDriver: true }),
    ]).start();
    const timer = setTimeout(hide, type === 'error' ? duration + 2000 : duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, title, message]);

  const hide = () => {
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: offset, duration: 180, useNativeDriver: true }),
      Animated.timing(opacityAnim, { toValue: 0, duration: 180, useNativeDriver: true }),
    ]).start(() => onClose());
  };

  if (!visible) return null;

  const visual = type === 'message'
    ? notificationVisual(action?.channel, action?.data)
    : { icon: STATUS[type].icon, color: STATUS[type].color() };
  const tappable = !!action?.onPress;

  const card = (
    <View style={[styles.card, { flexDirection: rtl.rowDirection }, rtl.isRTL ? { paddingRight: spacing.md + 2, paddingLeft: spacing.sm } : { paddingLeft: spacing.md + 2, paddingRight: spacing.sm }]}>
      <View style={[styles.accent, rtl.isRTL ? styles.accentEnd : styles.accentStart, { backgroundColor: visual.color }]} />
      <View style={[styles.iconWrap, { backgroundColor: withAlpha(visual.color, 0.14) }]}>
        <Ionicons name={visual.icon as any} size={20} color={visual.color} />
      </View>
      <View style={styles.content}>
        {!!title && <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{title}</Text>}
        {!!message && <Text style={[styles.message, { textAlign: rtl.textStart }]} numberOfLines={3}>{message}</Text>}
      </View>
      <TouchableOpacity onPress={hide} style={styles.close} hitSlop={8} accessibilityRole="button" accessibilityLabel="Close">
        <Ionicons name="close" size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>
    </View>
  );

  return (
    <Animated.View
      style={[styles.container, { opacity: opacityAnim, transform: [{ translateY: slideAnim }] }]}
      pointerEvents="box-none"
    >
      {tappable ? (
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => { action!.onPress(); hide(); }}
          accessibilityRole="button"
        >
          {card}
        </TouchableOpacity>
      ) : card}
    </Animated.View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    position: 'absolute',
    zIndex: 999999,
    elevation: 999999,
    ...(isDesktopWeb
      ? { bottom: spacing.lg, right: spacing.lg, width: 380 }
      : { top: Platform.OS === 'ios' ? 50 : Platform.OS === 'android' ? 30 : spacing.sm, left: spacing.sm, right: spacing.sm }),
  } as any,
  card: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm + 2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.14,
    shadowRadius: 16,
    elevation: 10,
  } as any,
  accent: { position: 'absolute', top: 0, bottom: 0, width: 4 },
  accentStart: { left: 0 },
  accentEnd: { right: 0 },
  iconWrap: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  content: { flex: 1, minWidth: 0, gap: 2 },
  title: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  message: { fontSize: typography.fontSize.sm, lineHeight: 19, color: colors.text.secondary },
  close: { padding: spacing.xs },
});
